<template>
	<BlockVisibilityObserver class="kova-lp-footer-observer" title="FOOTER">
		<footer class="kova-lp-footer" :class="`kova-lp-footer_theme_${theme}`">
			<div class="kova-lp-footer__panel" :class="`kova-lp-footer__panel_layout_${layout}`">
				<!-- The two columns of the `columns` layout. Anywhere else they add no box
				     (display: contents) and every item places itself, as it always has. -->
				<div class="kova-lp-footer__main">
					<div class="kova-lp-footer__brand">
						<img alt="Kova" class="kova-lp-footer__logo" :src="logo" />
						<p class="kova-lp-footer__tagline">{{ tagline }}</p>
					</div>

					<!-- Decorative badges, not links — the design has no store destinations. -->
					<div aria-hidden="true" class="kova-lp-footer__stores">
						<img alt="" class="kova-lp-footer__store" :src="appStoreBadge" />
						<img alt="" class="kova-lp-footer__store" :src="googlePlayBadge" />
					</div>

					<div class="kova-lp-footer__seals" :class="{ 'kova-lp-footer__seals_paired': madeInUsa }">
						<img alt="LegitScript certified" class="kova-lp-footer__seal" :src="seal" />
						<img v-if="madeInUsa" alt="Made in USA" class="kova-lp-footer__badge" :src="badgeMadeInUsa" />
					</div>

					<p class="kova-lp-footer__disclaimer">{{ disclaimer }}</p>
				</div>

				<div class="kova-lp-footer__aside">
					<ul class="kova-lp-footer__menu">
						<li v-for="link in links" :key="link.label" class="kova-lp-footer__menu-item">
							<a :href="link.href" rel="noopener" target="_blank">{{ link.label }}</a>
						</li>
					</ul>

					<p class="kova-lp-footer__contacts">
						<a :href="`mailto:${contacts.email}`">{{ contacts.email }}</a>
						<a :href="`tel:${contacts.phone.replace(/\s/g, '')}`">{{ contacts.phone }}</a>
					</p>

					<p class="kova-lp-footer__entity">
						<span v-for="(line, index) in entity" :key="index" v-text="line" />
					</p>
				</div>

				<!-- One vector for both breakpoints: it scales, and the two exports differed
				     only in aspect (4.30 vs 4.24), which is under a pixel and a half at
				     phone width. -->
				<img aria-hidden="true" alt="" class="kova-lp-footer__watermark" :src="watermark" />
			</div>
		</footer>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'

import appStoreBadge from '@/assets/kova/landing/badge-app-store.svg'
import badgeMadeInUsa from '@/assets/kova/landing/usa/badge-made-in-usa.png'
import googlePlayBadge from '@/assets/kova/landing/badge-google-play.svg'
import logo from '@/assets/kova/landing/logo-kova-footer.svg'
import seal from '@/assets/kova/landing/badge-legitscript.png'
import watermark from '@/assets/kova/landing/footer-watermark-desktop.svg'

export default {
	name: 'LandingKovaFooter',

	components: { BlockVisibilityObserver },

	props: {
		theme: {
			type: String,
			default: 'default',
			validator: (value) => ['default', 'v2'].includes(value),
		},

		// { email, phone } — rendered as mailto/tel links.
		contacts: {
			type: Object,
			required: true,
		},

		disclaimer: {
			type: String,
			required: true,
		},

		// Registered-entity block, one string per rendered line.
		entity: {
			type: Array,
			required: true,
		},

		// `grid` — the base footer (4843:27214 / 5026:7414, three columns on desktop);
		// `columns` — the current library footer (6390:38503) the USA frames use: 86 under the
		// disclaimer on mobile, and on desktop copy on the left with the links, contacts and
		// entity block down a narrow right column.
		layout: {
			type: String,
			default: 'grid',
			validator: (value) => ['grid', 'columns'].includes(value),
		},

		// [{ label, href }]
		links: {
			type: Array,
			required: true,
		},

		// The round "Made in USA" badge beside the LegitScript seal (USA, 6346:46753).
		madeInUsa: {
			type: Boolean,
			default: false,
		},

		tagline: {
			type: String,
			required: true,
		},
	},

	setup() {
		return { appStoreBadge, badgeMadeInUsa, googlePlayBadge, logo, seal, watermark }
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 4843:27214): the dark panel sits on the CTA block's blue, so the
// section keeps that colour behind its rounded top corners.
.kova-lp-footer {
	font-family: var(--kova-lp-font);
	background: var(--kova-lp-cta-panel); // the final-CTA panel it butts against
}

.kova-lp-footer__panel {
	position: relative;
	// Own stacking context, so the watermark's negative z-index stays above this background.
	isolation: isolate;
	overflow: hidden;
	box-sizing: border-box;

	display: flex;
	flex-direction: column;
	align-items: start;
	gap: var(--size-40);
	padding: var(--size-32) var(--size-20) 120px;

	border-start-start-radius: var(--size-16);
	border-start-end-radius: var(--size-16);
	background: #232c33; // footer surface — no Growth-DS token
}

// The library footer (6346:46753) ends 86 under the disclaimer, not the base frame's 120 —
// the watermark rides the last line.
.kova-lp-footer__panel_layout_columns {
	padding-bottom: 86px;
}

// Column wrappers of the `columns` desktop layout; until then they add no box.
.kova-lp-footer__main,
.kova-lp-footer__aside,
.kova-lp-footer__seals {
	display: contents;
}

.kova-lp-footer__brand {
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
}

.kova-lp-footer__logo {
	display: block;
	width: 136px;
	height: var(--size-36);
}

.kova-lp-footer__tagline {
	margin: 0;

	font-size: var(--size-16);
	line-height: 1.3;

	color: #607585; // footer muted — no Growth-DS token
}

.kova-lp-footer__seal {
	order: 3;

	display: block;
	width: 73px;
	height: 79px;
}

// With a second badge the seals form one row (USA, 6346:46753): the LegitScript seal, then a
// 76px round badge. The row takes the seal's place in the column.
.kova-lp-footer__seals_paired {
	order: 3;

	display: flex;
	align-items: center;
	gap: var(--size-32);

	.kova-lp-footer__seal {
		order: 0;
	}
}

.kova-lp-footer__badge {
	display: block;
	width: 76px;
	height: 76px;

	border-radius: 50%;
}

.kova-lp-footer__menu {
	order: 1;

	display: flex;
	flex-direction: column;
	gap: var(--size-12);
	margin: 0;
	padding: 0;

	font-size: var(--size-14);
	line-height: 1.4;

	color: #cad4dc; // footer link — no Growth-DS token
	list-style: none;
}

.kova-lp-footer__menu-item a {
	color: inherit;
	text-decoration: none;

	// Only under a real pointer: on a touch screen :hover sticks to the last tapped link
	// and left it underlined.
	@media (hover: hover) {
		&:hover {
			text-decoration: underline;
		}
	}
}

.kova-lp-footer__contacts {
	order: 2;

	display: flex;
	flex-direction: column;
	margin: 0;

	font-size: var(--size-16);
	line-height: 1.4;

	a {
		color: #cad4dc;
		text-decoration: underline; // explicit — the global reset drops it
	}
}

.kova-lp-footer__stores {
	order: 4;

	display: flex;
	align-items: center;
	gap: var(--size-16);
	height: var(--size-48);
}

// Both are the official store badges, used at their own aspect.
.kova-lp-footer__store {
	display: block;
	width: auto;
	height: var(--size-48);
}

.kova-lp-footer__disclaimer,
.kova-lp-footer__entity {
	margin: 0;

	font-size: var(--size-14);
	line-height: 1.4;

	color: #607585;
}

// The entity block is set as separate lines in Figma, with a blank one before the
// address — kept as an array so the break stays content, not markup.
.kova-lp-footer__entity {
	order: 5;

	display: flex;
	flex-direction: column;
}

// The blank entry renders as an empty span, and an empty flex item has no line box,
// so on its own it collapsed to zero height and the promised gap never appeared.
// `em` rather than a fixed value: it tracks the 1.4 line-height above through the
// desktop font-size bump.
.kova-lp-footer__entity span:empty {
	height: 1.4em;
}

.kova-lp-footer__disclaimer {
	order: 6;
}

// A giant KOVA wordmark bled off the bottom edge. It sits under the copy: the mark scales with
// the panel while the padding under the text does not, so on wider phones and tablets it
// reaches the last lines of the disclaimer.
.kova-lp-footer__watermark {
	position: absolute;
	z-index: -1;
	left: 0;
	right: 0;
	bottom: -2.41px;

	display: block;
	width: 100%;
	height: auto;
}

// Desktop (Figma 5026:7414): a three-column grid, full-bleed dark panel.
@media (min-width: 1024px) {
	.kova-lp-footer {
		background: var(--kova-lp-page);
	}

	// The panel is full-bleed, so it cannot cap its content the way the other
	// sections do — it carries the gutter itself. Figma's 160px gutter and 126px
	// column gap leave the brand column too narrow for the store badges once the
	// board drops below 1440, so both only apply from that width up.
	.kova-lp-footer__panel {
		display: grid;
		grid-template-columns: minmax(0, 1fr) max-content max-content;
		gap: var(--size-32) var(--size-64);
		align-items: start;
		padding: var(--size-100) var(--size-40) 0;

		border-start-start-radius: var(--size-36);
		border-start-end-radius: var(--size-36);
	}

	@media (min-width: 1440px) {
		.kova-lp-footer__panel {
			column-gap: 126px;
			padding-inline: 160px;
		}
	}

	.kova-lp-footer__brand {
		grid-area: 1 / 1;
		justify-content: space-between;
		gap: var(--size-24);
		height: 188px;
	}

	.kova-lp-footer__logo {
		width: 136px;
		height: 36.3px;
	}

	.kova-lp-footer__tagline {
		font-size: var(--size-20);
		line-height: 1.5;
	}

	.kova-lp-footer__seal {
		grid-area: 1 / 2;
		order: 0;

		width: 121px;
		height: 131px;
	}

	.kova-lp-footer__seals_paired {
		grid-area: 1 / 2;
		order: 0;
	}

	.kova-lp-footer__menu {
		grid-area: 1 / 3;
		order: 0;

		font-size: var(--size-20);
	}

	// The desktop footer frame was replaced by a component and no longer exists in
	// the file, so the contacts sit under the menu they belong with — our call, not
	// something read off a frame.
	.kova-lp-footer__contacts {
		grid-area: 2 / 3;
		order: 0;

		font-size: var(--size-20);
	}

	// The badges keep their own aspect and will not shrink, so on a narrow board
	// they wrap rather than running out of the brand column.
	.kova-lp-footer__stores {
		grid-area: 2 / 1;
		order: 0;

		flex-wrap: wrap;
		height: auto;
	}

	// 695px wide in Figma, which overruns the first column into the seal's — spanning
	// both columns gets the same measure without hard-coding an overflow.
	.kova-lp-footer__disclaimer {
		grid-area: 3 / 1 / 4 / 3;
		order: 0;

		max-width: 695px;
		padding-top: var(--size-32);

		font-size: var(--size-16);
		line-height: 1.5;
	}

	.kova-lp-footer__entity {
		grid-area: 3 / 3;
		order: 0;

		padding-top: var(--size-32);

		font-size: var(--size-16);
	}

	// In flow as its own full-width row, not absolutely positioned. The mark is
	// `width: 100%; height: auto`, so it gets taller as the board widens, while the
	// space reserved for it under the text used to be a fixed 398px bottom padding —
	// past ~1900px the two drifted apart and the wordmark climbed over the disclaimer
	// and the entity block. A grid row always reserves exactly the mark's own height.
	//
	// At 1440 this lands identically to the old absolute placement: 32px row gap +
	// 40px margin + 335px mark - 9px bleed = the 398px the padding used to hold.
	.kova-lp-footer__watermark {
		position: static;
		grid-area: 4 / 1 / 5 / 4;
		// The mark is cropped by the frame's bottom edge — 9px of its 335 at 1440.
		// Stated as the visible slice's ratio rather than a -9px margin, so the crop
		// stays proportional instead of drifting with the SVG's sub-pixel height. The
		// element is the image itself now, so the crop is `object-fit` rather than the
		// wrapper's `overflow` it used to be.
		aspect-ratio: 1440 / 326;
		object-fit: cover;
		object-position: top;
		margin-top: var(--size-40);
		// Content width plus both gutters, pulled back out by one gutter each side. The
		// width has to be stated: an <img> is a replaced element, so unlike the <picture>
		// wrapper this used to be it does not stretch to the grid area, and a plain 100%
		// would ignore the negative margins and stop at the content edge. `max-width`
		// opts out of the global `img { max-width: 100% }` reset, which would otherwise
		// clamp it straight back to the content width.
		width: calc(100% + 2 * var(--size-40));
		max-width: none;
		margin-inline: calc(-1 * var(--size-40));

		// Nested rather than grouped with the panel's own 1440 overrides above: those
		// come earlier in the file and would lose to the rule right here.
		@media (min-width: 1440px) {
			width: calc(100% + 320px);
			margin-inline: -160px;
		}
	}

	// The current library footer (6390:38503 — USA frame 6202:58325). Two columns over the
	// 1200 content: brand, stores, seals and disclaimer on the left (724), the links, contacts
	// and entity block spread down a 376px right column, 100 apart. The side tracks are the
	// page gutters (120 at 1440, never under 40), so the watermark row spans the whole panel
	// without pulling itself out through them.
	.kova-lp-footer__panel_layout_columns {
		grid-template-columns:
			minmax(var(--size-40), 1fr) minmax(0, 724px) var(--size-100) 376px
			minmax(var(--size-40), 1fr);
		gap: 0;
		padding: var(--size-100) 0 0;

		.kova-lp-footer__main,
		.kova-lp-footer__aside {
			display: flex;
			flex-direction: column;
		}

		.kova-lp-footer__main {
			grid-area: 1 / 2;
			gap: var(--size-60);
		}

		.kova-lp-footer__aside {
			grid-area: 1 / 4;
			align-self: stretch;
			justify-content: space-between;
		}

		.kova-lp-footer__brand {
			justify-content: start;
			gap: var(--size-32);
			height: auto;
		}

		// The stores close the brand group 40 under the tagline; the column's other gaps are 60.
		.kova-lp-footer__stores {
			margin-top: calc(var(--size-40) - var(--size-60));
		}

		.kova-lp-footer__seals_paired {
			align-items: start;
			gap: 41px; // 6390:38585
		}

		.kova-lp-footer__seal {
			width: 92px;
			height: var(--size-100);
		}

		.kova-lp-footer__badge {
			width: var(--size-100);
			height: var(--size-100);
		}

		.kova-lp-footer__disclaimer {
			max-width: none;
			padding-top: 0;
		}

		.kova-lp-footer__menu,
		.kova-lp-footer__contacts,
		.kova-lp-footer__entity {
			white-space: nowrap;
		}

		.kova-lp-footer__contacts {
			font-size: var(--size-16);
		}

		.kova-lp-footer__entity {
			padding-top: 0;
		}

		// 81 under the copy at 1440 (watermark top 761 in the 1088 frame) and cut by the bottom
		// edge to its visible 327 of 335.
		.kova-lp-footer__watermark {
			grid-area: 2 / 1 / 3 / -1;
			aspect-ratio: 1440 / 327;
			width: 100%;
			margin: 81px 0 0;
		}
	}
}

// DEV-35932 uses the existing columns footer (5058:9345 / 6357:61166).
.kova-lp-footer_theme_v2 {
	background: var(--kova-lp-page);
	.kova-lp-footer__stores {
		flex-wrap: wrap;
		height: auto;
	}
	.kova-lp-footer__watermark {
		max-width: 375px;
	} // 6357:61166
}

@media (min-width: 1024px) {
	.kova-lp-footer_theme_v2 .kova-lp-footer__watermark {
		max-width: none;
	}
}
</style>
